
import React from "react";
import { Badge, Button, Message, Space, Tag } from "@bedrock/components";
import { Copy, FileCodeO, Invoice } from "@bedrock/icons-react";
import copy from 'copy-to-clipboard';
import { commonFilterOptions, enums, outsourceCommonFilterOptions } from "services/api";
import classNames from 'classnames/bind';
import styles from './index.less';
import { financial } from "utils/number";
import { FilterType } from "@bedrock/eed-react/lib/components/ProFilter/interface";
import { isBlank } from "@bedrock/utils";
import DotDotDot from "#components/DotDotDot";
import CopyableNode from "#components/CopyableNode";
import ModalTip from "#components/ModalTip";
import AmountOrder from "#components/AmountOrder";
import { $sType, localPx2rem } from "#utils";
import _ from 'lodash';
import { formatTruefalseDisplay } from "#features/utils";
import { CurrencyType } from "#components";
import { ORDER_LEVEL_LIST } from "#utils/CONST";

export const CONTRACT_TEMPLATE_OPTIONS = [
  { name: "运营合同", value: "operation" },
  { name: "素材合同", value: "material" },
  { name: "达人合同", value: "kol" },
  { name: "传播合同", value: "spread" },
]

const blankDefault = (v: any) => {
  return isBlank(v) ? '--' : v;
}

const cx = classNames.bind(styles);

export const FILTER_OPTIONS: { filterKeys: Array<string | { optionKey: string, searchKey: string }> } = {
  filterKeys: [
    'demand_categories', // 需求类型
    'product', // 产品
    'principals', // 需求人员
    'priorities', // 优先级
  ],
};

export const getCopyNode = (v: string, trigger?: React.ReactElement) => {
  return (
    <Space size={8}>
      <div>{trigger || v || '--'}</div>
      {v && (
        <Copy
          onClick={(e) => {
            e.stopPropagation();
            copy(v);
            Message.success({ content: '复制成功' });
          }}
        />
      )}
    </Space>
  );
};

export const getColumnsConfig = () => {
  return {
    checkbox: {
      width: 40,
      fixed: 'left',
    },
    principal_names: {
      align: 'left',
      width: 160,
      render: (v: any, r: any, i: number) => {
        const text = Array.isArray(v) ? v.join('，') : v;
        return <DotDotDot clamp={2}>{text}</DotDotDot>;
      },
    },
    expect_begin_at: {
      width: 132,
      sortable: true,
    },
    expect_end_at: {
      width: 132,
      sortable: true,
    },
    planner_names: {
      align: 'left',
      width: 200,
      render: (v: any, r: any, i: number) => {
        const text = Array.isArray(v) ? v.join('，') : v;
        return isBlank(text) ? '--' : <DotDotDot clamp={2}>{text}</DotDotDot>;
      },
    },
    plan_type: {
      align: 'left',
      width: 200,
    },
    have_quote_supplier_num: {
      width: 168,
    },
    quote_deadline: {
      width: 168,
    },
    formatted_quote_deadline: {
      width: 168,
    },
    supplier_names: {
      align: 'left',
      width: 200,
      render: (v: any, r) => {
        const text = Array.isArray(v) ? v.join('，') : v;
        return blankDefault(text);
      },
    },
    project_plan_info_budget: {
      align: 'right',
      width: 170,
      render: (v: any, r: any, i: number) => {
        return financial(v || 0);
      },
    },
    human_state_name: {
      width: 140,
    },
    id: {
      width: 104,
      fixed: 'left',
      render: (v: any, r: any, i: number) => {
        return (
          <CopyableNode value={v} tipToolTitle="复制单号" >
            <Button
              type='link'
              onClick={() => window.open(`/#/channelDistributionWorkbench/channelDistributionGroup/projectDetailSingle/${v}`)}
            >
              {v}
            </Button>
          </CopyableNode>
        );
      },
    },
    product_identifier: {
      width: 104,
    },
    name: {
      width: 400,
      // 列宽可调整
      resizable: true,
      render: (v: any, r: any, i: number) => {
        const { source_id , urgent } = r;
        return (
          <div className="two-rows-ellipsis" title={v}>
            {!!source_id && (
              <Tag
                color='light-orange'
                size='xSmall'
                style={{ marginRight: localPx2rem(4), verticalAlign: 'baseline' }}
                text="旧"
              />
            )}
            {urgent && <Tag
                color='red'
                size='xSmall'
                style={{ marginRight: localPx2rem(4), verticalAlign: 'baseline' }}
                text="加急验收"
              />}
            {blankDefault(v)}
          </div>
        );
      },
    },
    category_names: {
      width: 280,
      align: 'left',
    },
    demand_category: {
      width: 280,
      align: 'left',
    },
    priority: {
      width: 104,
      className: 'no-vertical-padding',
      render: (v: any, r: any, i: number) => {
        // 0 {紧急} 1 {高} 2 {中} 3 {低} other {--}
        const keyMap = {
          0: <Tag text="紧急" size={'small'} color="light-red" />,
          1: <Tag text="高" size={'small'} color="light-orange" />,
          2: <Tag text="中" size={'small'} color="light-blue" />,
          3: <Tag text="低" size={'small'} color="light-grey" />,
        }
        return keyMap[v] || '--';
      },
    },
    plan_info_budget: {
      width: 160,
      align: 'right',
      render: (v, r, i) => {
        return (
          <CurrencyType
            view
            value={{
              value: v,
              currency: undefined,
            }}
            showCurrency={false}
            currencyColor={'black'}
          />
        )
      }
    },
    quote_price: {
      width: 160,
      align: 'right',
      render: (v, r, i) => {
        return (
          <CurrencyType
            view
            value={{
              value: v,
              currency: undefined,
            }}
            showCurrency={false}
            currencyColor={'black'}
          />
        )
      }
    },
    settle_price: {
      width: 160,
      align: 'right',
      render: (v, r, i) => {
        return (
          <CurrencyType
            view
            value={{
              value: v,
              currency: undefined,
            }}
            showCurrency={false}
            currencyColor={'black'}
          />
        )
      }
    },
    oa_id: {
      // OA流程号
      width: 240,
      align: 'left',
      render: (v: any, r: any, i: number) => {
        return !!v && (
          <CopyableNode value={v} tipToolTitle="复制OA流程单号" >
            <Button
              onClick={() => window.open(r?.['oa_url'])}
              type='link'
            >
              {v}
            </Button>
          </CopyableNode>
        )
      }
    },
    over_amount_oa_id: {
      // OA流程号
      width: 240,
      align: 'left',
      render: (v: any, r: any, i: number) => {
        return !!v && (
          <CopyableNode value={v} tipToolTitle="复制OA流程单号" >
            <Button
              onClick={() => window.open(r?.['oa_url'])}
              type='link'
            >
              {v}
            </Button>
          </CopyableNode>
        )
      }
    },
    translated_oa_status: {
      // OA状态
      width: 104,
      align: 'left',
      render: (v: any, r: any, i: number) => {
        if (v === undefined && r.state === 'DEMANDER_ACCEPT') {
          return '--';
        }
        // 新枚举值，未提交，已提交，已驳回，已归档
        const text = v || '未提交';
        switch (text) {
          case '未提交':
            return <Tag text={text} size={'small'} color="light-brand" />;
          case '已提交':
            return <Tag text={text} size={'small'} color="light-blue" />;
          case '已驳回':
            return <Tag text={text} size={'small'} color="light-red" />;
          case '已归档':
            return <Tag text={text} size={'small'} color="light-green" />;
          default:
            return <Tag text={text} size={'small'} color="light-orange" />;
        }
      },
    },
    oa_rejection_reason: {
      // 驳回原因
      width: 240,
      align: 'left',
      render: (v: any, r: any, i: number) => {
        return <div className="two-rows-ellipsis" title={v}>{blankDefault(v)}</div>;
      }
    },
    translated_purchasing_method: {
      // 采购方式,翻译全名
      width: 200,
      align: 'left',
    },
    translated_bidding_status: {
      // 招采状态，只在招采申请中
      width: 180,
      align: 'left',
    },
    translated_return_status: {
      // 退回状态，"报价待商务确认"
      width: 180,
      align: 'left',
      render: (v: any, r: any, i: number) => {
        if (!v) return blankDefault(v);
        return <Tag text={v} size={'small'} color="light-red" />;
      },
    },
    reimburse_status: {
      width: 160,
      align: 'left',
      render: (v: string) => {
        const colorMap = {
          "0": 'warning',
          "1": '#5154E0',
          "2": 'tips',
          "3": 'success',
          "-1": 'error',
        }
        const state = enums.ReimburseStatusEnum.pick(v)
        const val = state?.value + ""
        if (v === null || v === undefined) {
          return <div>--</div>
        }
        return <Badge dot dotStatus={colorMap[val]} style={{ background: `${colorMap[val]}` }} text={state?.name} />
      }
    },
    paperless_flag: {
      width: 160,
      align: 'left',
      render: (v: boolean) => {
        switch (v) {
          case true:
            return <Tag
              color={'light-green'}
              // FileCodeO
              text={<Space align={'center'} size={2}><FileCodeO /> 无纸化</Space>}
            />
          case false:
            return <Tag
              color={'light-grey'}
              text={<Space align={'center'} size={2}><Invoice /> 纸质</Space>}
            />
          default:
            return '--'
        }
      }
    },
    no_contract_flag: {
      width: 160,
      align: 'left',
      render: (v: boolean) => {
        return formatTruefalseDisplay(v);
      }
    },
    quote_score_state: {
      width: 160,
      align: 'left',
      render: (v: string) => {
        const record = enums.QuoteScoreStateEnum.pick(v);
        if (!record?.name) {
          return '--'
        }
        const colorMap = {
          "NOT_SCORED": 'light-orange',
          "SCORING": 'light-blue',
        }
        return <Tag color={colorMap[record.value] || 'light-blue'} text={record.name} size='small' />
      }
    },
    translated_approve_type: {
      // 审批类型
      width: 160,
      align: 'left',
    },
    operation: {
      width: 80,
      fixed: 'right',
      render: (v: any, r: any, i: number) => {
        return (
          <Space size={12}>
            {(r['can_move?'] || r['can_move']) && (r['state'] || '').toLowerCase() !== 'closed' && (
              <Button type='link'>处理</Button>
            )}
            {(r['can_destroy?'] || r['can_destroy']) && (
              <ModalTip
                link
                componentClass={Button}
                modalConfig={{
                  title: '您确定删除此需求单？',
                  onOk: () => {
                    // return deleteDemand(r.id, { menuKey });
                  },
                }}
              >
                删除
              </ModalTip>
            )}
          </Space>
        );
      },
    },
    supplier_quote_price: {
      width: 160,
      align: 'right',
      render: (v, r, i) => {
        return (
          <CurrencyType
            view
            value={{
              value: v,
              currency: undefined,
            }}
            showCurrency={false}
            currencyColor={'black'}
          />
        )
      }
    },
    // 项目编号
    number: {
      width: 120,
      render: (v: any, r: any, i: number) => {
        return <CopyableNode value={v} tipToolTitle="复制" />;
      },
    },
  };
}

export const defaultColumns = [
  {
      "name": "单号",
      "key": "id"
  },
  {
      "name": "所属产品",
      "key": "product_identifier"
  },
  {
      "name": "需求标题",
      "key": "name"
  },
  {
      "name": "优先级",
      "key": "priority"
  },
  {
      "name": "需求类型",
      "key": "demand_category"
  },
  {
      "name": "需求人员",
      "key": "principal_names"
  },
  {
      "name": "审批类型",
      "key": "translated_approve_type"
  },
  {
      "name": "OA流程号",
      "key": "oa_id"
  },
  {
      "name": "OA状态",
      "key": "translated_oa_status"
  },
  {
      "name": "驳回原因",
      "key": "oa_rejection_reason"
  },
  {
      "name": "预计开始日期",
      "key": "expect_begin_at"
  },
  {
      "name": "预计发布日期",
      "key": "expect_end_at"
  },
  {
      "name": "操作列",
      "key": "operation"
  }
]


export const FILTER_ITEM_CONF = {
  principals: {
    name: '需求人员',
    value: 'principals',
    type: FilterType.SELECT_MULTIPLE,
    filterComponentProps: { 
      returnOnlyLeaf: true 
    },
  },
  product: {
    name: '产品',
    value: 'product',
    type: FilterType.SELECT_MULTIPLE,
    filterComponentProps: {
      showSearch: true,
      returnOnlyLeaf: true 
    }
  },
  bidding_states: {
    name: '比选状态',
    value: 'bidding_states',
    type: FilterType.SELECT_MULTIPLE,
  },
  demand_categories: {
    name: '需求类型',
    value: 'product',
    type: FilterType.SELECT_MULTIPLE,
  },
  status: {
    name: '状态',
    value: 'status',
    type: FilterType.SELECT_MULTIPLE,
    filterComponentProps: {
      options: [
        { name: "启用", value: "1" },
        { name: "禁用", value: "0" },
      ]
    }
  },
  priorities: {
    name: '需求优先级',
    value: 'priorities',
    type: FilterType.SELECT_MULTIPLE,
  },
  bidding_types: {
    name: '比选类型',
    value: 'bidding_types',
    type: FilterType.SELECT_MULTIPLE,
  },
  bidding_types_v2: {
    name: '比选类型',
    value: 'bidding_types_v2',
    type: FilterType.SELECT_MULTIPLE,
  },
  bidding_methods: {
    name: '比选方式',
    value: 'bidding_methods',
    type: FilterType.SELECT_MULTIPLE,
  },
  active_suppliers: {
    mode: 'select',
    treeCheckable: true,
    isMultiple: true,
    isTree: true,
    searchEnable: true,
    typeConfig: {
      multiple: { showSelectAllOption: true },
    },
    filterComponentProps: {
      virtual: true,
    },
  },
  suppliers: {
    name: '供应商',
    value: 'priorities',
    type: FilterType.SELECT_MULTIPLE,
    filterComponentProps: { 
      virtual: true,
      returnOnlyLeaf: true 
    },
  },
  demand_suppliers: {
    name: '供应商',
    value: 'priorities',
    type: FilterType.SELECT_MULTIPLE,
    filterComponentProps: {
      virtual: true,
    },
  },
  demand_suppliers_new: {
    mode: 'filter',
    label: '供应商',
    treeCheckable: true,
    isMultiple: true,
    isTree: true,
    searchEnable: true,
    filterComponentProps: {
      virtual: true,
    },
    typeConfig: {
      multiple: { showSelectAllOption: true },
    },
  },
  currency: {
    mode: 'filter',
    saveKey: 'settle_currency',
    needOption: true,
  },
  active_products: {
    mode: 'filter',
    isMultiple: true,
    needOption: true,
  },
  plan_users: {
    mode: 'filter',
    treeCheckable: true,
    isMultiple: true,
    searchEnable: true,
  },
  demand_plan_users: {
    name: '策划',
    value: 'demand_plan_users',
    type: FilterType.SELECT_MULTIPLE,
  },
  demand_business_users: {
    name: '商务',
    value: 'demand_business_users',
    type: FilterType.SELECT_MULTIPLE,
  },
  purchase_ways: {
    mode: 'filter',
    isMultiple: true,
    needOption: true,
  },
  no_contract_flag: {
    name: '是否无合同事项',
    value: 'no_contract_flag',
    type: FilterType.SELECT,
  },
  miss_contract_no: {
    name: '是否未填写合同编号',
    value: 'miss_contract_no',
    type: FilterType.SELECT,
  },
  over_amount_flag: {
    name: '是否超额',
    value: 'oa_status',
    type: FilterType.SELECT,
    filterComponentProps: {
      options: [
        { value: false, name: '否' },
        { value: true, name: '是' },
      ],
    }
  },
  oa_status: {
    name: 'OA状态',
    value: 'oa_status',
    type: FilterType.SELECT_MULTIPLE,
  },
  over_amount_oa_status: {
    // 实际指 需求方验收时发起的超额审批OA状态
    name: 'OA状态',
    value: 'over_amount_oa_status',
    type: FilterType.SELECT_MULTIPLE,
  },
  paperless_flag: {
    name: '无纸化标识',
    value: 'paperless_flag',
    type: FilterType.SELECT,
  },
  reimburse_status: {
    name: '付款状态',
    value: 'reimburse_status',
    type: FilterType.SELECT_MULTIPLE,
  },
  quote_score_states: {
    name: '评分状态',
    value: 'pre_approve_type',
    type: FilterType.SELECT_MULTIPLE,
  },
  pre_approve_type: {
    name: '审批类型',
    value: 'pre_approve_type',
    type: FilterType.SELECT_MULTIPLE,
  },
  // -------------------- trumpet ----------------------------
  oa_borrow_workflow_id: {
    name: 'OA借款流程ID',
    value: 'oa_borrow_workflow_id',
    type: FilterType.TEXT,
  },
  l1_category_id: {
    name: '需求类型',
    value: 'l1_category_id',
    type: FilterType.SELECT_MULTIPLE,
  },
  planners: {
    name: '策划人员',
    value: 'planners',
    type: FilterType.SELECT_MULTIPLE,
  },
  // -------------------- external outWorkbenchPortal/outNormalPurchase ----------------------------
  quote_deadline_range: {
    name: '报价截止时间',
    value: 'quote_deadline_range',
    type: FilterType.DATE_TIME_RANGE,
  },
  expect_end_at_range: {
    name: '预计发布时间',
    value: 'expect_end_at_range',
    type: FilterType.DATE_RANGE,
  },
  backed: {
    name: '仅展示被退回',
    value: 'backed',
    type: FilterType.CHECKBOX,
  },
  // -------------------- external outWorkbenchPortal/outNormalPurchase ----------------------------
  state_list: {
    name: '状态',
    value: 'state_list',
    type: FilterType.SELECT_MULTIPLE,
  },
  invite_confirm_end_time_range: {
    name: '接受邀约截止日期',
    value: 'invite_confirm_end_time_range',
    type: FilterType.DATE_RANGE,
  },
  submit_plan_end_time_range: {
    name: '提交方案截止日期',
    value: 'submit_plan_end_time_range',
    type: FilterType.DATE_RANGE,
  },
  // -------------------- contractTemplate ----------------------------
  types: {
    name: '合同类型',
    value: 'types',
    type: FilterType.SELECT_MULTIPLE,
    filterComponentProps: {
      options: CONTRACT_TEMPLATE_OPTIONS,
    }
  },
  // -------------------- myWorkBench/myWorkBenchDemand ----------------------------
  levels: {
    name: '单据层级',
    value: 'levels',
    type: FilterType.SELECT_MULTIPLE,
  },
  demand_states: {
    name: '状态',
    value: 'trumpet_states',
    type: FilterType.SELECT_MULTIPLE,
  },
  number: {
    name: '项目编号',
    value: 'number',
    type: FilterType.TEXT,
    filterComponentProps: {
      style: { width: 240 },
    }
  },
  purchasing_method_and_strategy_params: {
    name: '采购方式',
    value: 'purchasing_method_and_strategy_params',
    type: FilterType.SELECT_MULTIPLE,
  },
  business_users: {
    name: '商务人员',
    value: 'business_users',
    type: FilterType.SELECT_MULTIPLE,
  },
  supplier_id_list: {
    name: '供应商',
    value: 'supplier_id_list',
    type: FilterType.SELECT_MULTIPLE,
    filterComponentProps: { 
      virtual: true,
      returnOnlyLeaf: true 
    },
  },
  group_types: {
    name: '需求接收部门',
    value: 'group_types',
    type: FilterType.SELECT_MULTIPLE,
    filterComponentProps: {
      options: [
        { name: '	渠道发行中心', value: 'CHANNEL_GROUP' },
        { name: '市场中心', value: 'MARKET_GROUP' },
      ]
    }
  },
  urgent: {
    name: '加急验收',
    value: 'urgent',
    type: FilterType.CHECKBOX,
  },
  show_return_only: {
    name: '仅展示被退回',
    value: 'show_return_only',
    type: FilterType.CHECKBOX,
  },
  execution_start_time: {
    name: '执行开始时间',
    value: 'execution_start_time',
    type: FilterType.DATE_RANGE,
  },
  execution_end_time: {
    name: '执行结束时间',
    value: 'execution_end_time',
    type: FilterType.DATE_RANGE,
  },
  
  closed_loop_states: {
    name: '状态',
    value: 'closed_loop_states',
    type: FilterType.SELECT_MULTIPLE,
  },
  closed_loop_search_demand_categories: {
    name: '状态',
    value: 'closed_loop_search_demand_categories',
    type: FilterType.SELECT_MULTIPLE,
  },
   closed_loop_principals: {
    name: '状态',
    value: 'closed_loop_principals',
    type: FilterType.SELECT_MULTIPLE,
  },
  // -------------------- myWorkBench/myWorkBenchTrumpetDemand ----------------------------
  trumpet_states: {
    name: '状态',
    value: 'trumpet_states',
    type: FilterType.SELECT_MULTIPLE,
  },
  trumpet_search_demand_categories: {
    name: '状态',
    value: 'trumpet_search_demand_categories',
    type: FilterType.SELECT_MULTIPLE,
  },
  trumpet_principals: {
    name: '状态',
    value: 'trumpet_principals',
    type: FilterType.SELECT_MULTIPLE,
  },
  expect_begin_at_range: {
    name: '预计开始日期',
    value: 'expect_begin_at_range',
    type: FilterType.DATE_RANGE,
  },
  // -------------------- myWorkBench/myWorkBenchBidding ----------------------------
  expect_start_date_range: {
    name: '预计开始日期',
    value: 'expect_start_date_range',
    type: FilterType.DATE_RANGE,
  },
  expect_end_date_range: {
    name: '预计结束日期',
    value: 'expect_end_date_range',
    type: FilterType.DATE_RANGE,
  },
  created_at_range: {
    name: '创建日期',
    value: 'created_at_range',
    type: FilterType.DATE_RANGE,
  },
  // -------------------- supplierStore/supplierStoreStored ----------------------------
  product_ids: {
    name: '所属产品',
    value: 'product_ids',
    type: FilterType.SELECT_MULTIPLE,
    filterComponentProps: {
      showSearch: true,
      returnOnlyLeaf: true 
    }
  },
  l1_category_ids: {
    name: '需求类型(一级)',
    value: 'l1_category_ids',
    type: FilterType.SELECT_MULTIPLE,
  },
  create_date_range: {
    name: '创建日期',
    value: 'create_date_range',
    type: FilterType.DATE_RANGE,
  },
  publish_data_range: {
    name: '执行时间',
    value: 'publish_data_range',
    type: FilterType.DATE_RANGE,
  },
  channel_types: {
    name: '服务类型',
    value: 'channel_types',
    type: FilterType.SELECT_MULTIPLE,
  },
  relate_main: {
    name: '查看关联主体',
    value: 'relate_main',
    type: FilterType.CHECKBOX,
  },
  relate_channel_types: {
    name: '关联主体服务类型',
    value: 'relate_channel_types',
    type: FilterType.SELECT_MULTIPLE,
  },
  relate_main_supplier_ids: {
    name: '关联主体',
    value: 'relate_main_supplier_ids',
    type: FilterType.SELECT_MULTIPLE,
  },
  purchase_methods: {
    name: '采购方式',
    value: 'purchase_methods',
    type: FilterType.SELECT_MULTIPLE,
  },
  states: {
    name: '状态',
    value: 'states',
    type: FilterType.SELECT_MULTIPLE,
  },
  // --------------------------------------------------- supplierGroup/supplierIndicator --------------------------------
  category_ids: {
    name: '费用类型',
    value: 'category_ids',
    type: FilterType.SELECT_MULTIPLE,
  }
};

/** 获取key return typeof key === 'string' ? key : key?.[keyName] */
export const getKey = (key, keyName) => {
  return typeof key === 'string' ? key : key?.[keyName];
}

export const defaultFilterOptionsApi = (props) => {
  const { menuKey, group_type = 'CHANNEL_GROUP', filterOptions = FILTER_OPTIONS } = props;
  const { filterKeys = [] } = filterOptions;
  const keys = filterKeys.filter(el => !el.noFetchOption).map(key => getKey(key, 'optionKey'));

  return $sType === 'outer' ? 
    outsourceCommonFilterOptions({
      outsource_state: menuKey === undefined ? menuKey : (_.snakeCase(menuKey)).toUpperCase() as any,
      keys,
    })
    : 
    commonFilterOptions({
      demand_state: (_.snakeCase(menuKey || '')).toUpperCase() as any,
      group_type: group_type as any,
      keys,
    });
}

export const OPTION_ALL = { name: '全选', value: '__SELECT_ALL__' };

/** 获取筛选条件-当接口获取的下拉项大于（默认10）时，添加全选选项 */
export const getFilterOptions = ({ fetchedFilterOptions, localOptions, paramOptions, compareVal = 10 }) => {
  if (paramOptions?.length) {
    return paramOptions?.length > compareVal ? [OPTION_ALL, ...paramOptions] : paramOptions;
  }
  if (fetchedFilterOptions?.length > compareVal) {
    return [OPTION_ALL, ...fetchedFilterOptions];
  }
  return fetchedFilterOptions || localOptions || [];
}

/** 根据key对应的值获取筛选条件配置 */
export const findRecordFormMutilTreeByKey = (mtree, val, uniField = 'id') => {
  let record: any = {}
  const find = (mtree) => {
    mtree.some(item => {
      if (item[uniField] === val) {
        record = item;
        return true;
      }
      if (item.children?.length) {
        return find(item.children);
      }
    })
  }
  find(mtree);
  return record;
}

/** mpc 表格行文本置灰 class */
export const mpc_table_row_text_grey = 'mpc-table-row-text-grey';

/** 获取tab项 */
export const getTabItemsFormProps = (props: { childMenus: any[], menuCount: any[] }) => {
  const items = props?.childMenus?.map?.((menu: { name: any; key: string }) => {
    const badge = props?.menuCount?.find?.((countInfo: any) => countInfo.key === menu.key)?.count;
    return {
      ...menu,
      // tab: <div>{menu.name}<Badge count={badge} className="m-l-8"/></div>,
      tab: menu.name,
      extra: <Badge count={badge} />,
    }
  }) || [];
  return items;
}